import { useEffect, useMemo, useState } from "react";
import Top from "./Top";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import SLNScanHeader from "./SLNScanHeader";

const API_ALL_LATEST =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/OptionChain/GetAllLatestOptionChainHistory";

const INDEX_SYMBOLS = [
  { key: "NIFTY 50", symbol: "NSE:NIFTY50-INDEX", color: "#003087" },
  { key: "BANK NIFTY", symbol: "NSE:NIFTYBANK-INDEX", color: "#1565c0" },
  { key: "SENSEX", symbol: "BSE:SENSEX-INDEX", color: "#0277bd" },
  { key: "FINNIFTY", symbol: "NSE:FINNIFTY-INDEX", color: "#01579b" },
];

function injectCSS(id, css) {
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}

function fmt2(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(2);
}
function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/* ── Index Card ── */
function IndexCard({ c, loading }) {
  const down = Number(c.chg) < 0;
  const cfg = INDEX_SYMBOLS.find((x) => x.symbol === c.symbol) || {};

  return (
    <div className="idx-card">
      <div
        className="idx-card-accent"
        style={{ background: cfg.color || "#003087" }}
      />
      <div
        className="d-flex justify-content-between align-items-start mb-1"
        style={{ paddingLeft: 8 }}
      >
        <div className="idx-card-name">{c.key}</div>
        {c.ok ? (
          <span
            className={`idx-card-chg ${down ? "text-danger" : "text-success"}`}
          >
            {down ? "▼" : "▲"} {fmt2(c.chg)} ({fmt2(c.chgp)}%)
          </span>
        ) : (
          <span className="text-muted" style={{ fontSize: 12 }}>
            {loading ? "Loading…" : "—"}
          </span>
        )}
      </div>

      <div className="idx-card-ltp" style={{ paddingLeft: 8 }}>
        {c.ok ? (
          Number(c.ltp).toLocaleString("en-IN")
        ) : loading ? (
          <div
            className="skeleton"
            style={{ width: 120, height: 28, borderRadius: 6 }}
          />
        ) : (
          "—"
        )}
      </div>

      <div className="idx-card-hl" style={{ paddingLeft: 8 }}>
        High: &nbsp;<b>--</b>&nbsp;&nbsp; Low: &nbsp;<b>--</b>
      </div>

      <hr className="idx-card-divider" />

      <div
        className="d-flex justify-content-between"
        style={{ paddingLeft: 8 }}
      >
        <button
          className="idx-card-action"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <i className="bi bi-activity" /> OI Liner
        </button>
        <button
          className="idx-card-action"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <i className="bi bi-clock-history" /> OI History
        </button>
      </div>
    </div>
  );
}

/* ── Stock Mini Card ── */
function StockCard({ c, type, navigate }) {
  const isGain = type === "gainer";
  return (
    <div
      className={`stock-mini-card ${isGain ? "gainer" : "loser"}`}
      onClick={() =>
        navigate(
          `/option-chain?symbol=${encodeURIComponent(c.symbol)}&strikecount=100`,
        )
      }
    >
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <div className="stock-mini-name">{c.short}</div>
          <div className="stock-mini-pcr">VOL PCR: {fmt2(c.volPCR)}</div>
        </div>
        <button
          className="eye-btn"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/option-chain?symbol=${encodeURIComponent(c.symbol)}&strikecount=100`,
            );
          }}
        >
          <i className="bi bi-eye" />
        </button>
      </div>

      <div className="mt-2">
        <div className="stock-mini-ltp-label">LTP</div>
        <div className="stock-mini-ltp">{fmt2(c.ltp)}</div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <span style={{ fontSize: 11, color: "#a0aec0" }}>Change</span>
        {isGain ? (
          <span className="badge-gain">+{fmt2(c.changePct)}%</span>
        ) : (
          <span className="badge-loss">{fmt2(c.changePct)}%</span>
        )}
      </div>
    </div>
  );
}

function SLNScanLive() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexCards, setIndexCards] = useState([]);

  useEffect(() => {
    injectCSS("slnl-css", CSS);
  }, []);

  const { topGainers, topLosers } = useMemo(() => {
    const valid = rows.filter(
      (r) => typeof r.changePct === "number" && !Number.isNaN(r.changePct),
    );
    const sorted = [...valid].sort((a, b) => b.changePct - a.changePct);
    return {
      topGainers: sorted.slice(0, 12),
      topLosers: sorted.slice(-12).reverse(),
    };
  }, [rows]);

  async function loadAllCompanies() {
    try {
      setLoading(true);
      setIndexLoading(true);
      const token = localStorage.getItem("SLNToken");
      if (!token) throw new Error("Token not found.");

      const res = await fetch(`${API_ALL_LATEST}?page=1&limit=200`, {
        method: "GET",
        headers: { Authorization: token, "Content-Type": "application/json" },
      });
      const json = await res.json().catch(() => ({}));
      const list = Array.isArray(json?.rows) ? json.rows : [];

      const cleaned = list
        .map((r) => ({
          ...r,
          symbol: String(r.symbol || ""),
          short: String(r.short || ""),
          ltp: safeNum(r.ltp),
          ltpChange: safeNum(r.ltpChange),
          changePct: safeNum(r.changePct),
          volPCR: safeNum(r.volPCR),
        }))
        .filter((r) => r.symbol && r.short);

      setRows(cleaned);

      const bySymbol = new Map(cleaned.map((r) => [r.symbol, r]));
      const indexOut = INDEX_SYMBOLS.map((it) => {
        const r = bySymbol.get(it.symbol);
        if (!r) return { ...it, ok: false };
        return {
          ...it,
          ok: true,
          ltp: r.ltp ?? null,
          chg: r.ltpChange ?? null,
          chgp: r.changePct ?? null,
        };
      });
      setIndexCards(indexOut.filter((x) => x.ok));
    } catch (e) {
      console.error("loadAllCompanies failed", e);
      setRows([]);
      setIndexCards([]);
    } finally {
      setLoading(false);
      setIndexLoading(false);
    }
  }

  useEffect(() => {
    loadAllCompanies();
  }, []);

  return (
    <>
      <Top />
      <SLNScanHeader page="screener" />

      <div className="slnl-root">
        {/* ── Index Cards ── */}
        <section className="pt-5 mb-4">
          <div className="container">
            <div className="row g-2">
              {(indexCards.length ? indexCards : INDEX_SYMBOLS).map((c) => (
                <div className="col-12 col-md-6 col-xl-3" key={c.key}>
                  <IndexCard c={c} loading={indexLoading} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Gainers & Losers ── */}
        <section className="pb-6">
          <div className="container">
            <div className="row g-4">
              {/* Gainers */}
              <div className="col-12">
                <div className="slnl-section-label gainers">
                  <i className="bi bi-graph-up-arrow" />
                  Top Gainers
                </div>
                <div className="row g-2">
                  {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="col-lg-2 col-md-4 col-6">
                          <div
                            className="skeleton"
                            style={{ height: 120, borderRadius: 10 }}
                          />
                        </div>
                      ))
                    : topGainers.map((c) => (
                        <div key={c.symbol} className="col-lg-2 col-md-4 col-6">
                          <StockCard c={c} type="gainer" navigate={navigate} />
                        </div>
                      ))}
                </div>
              </div>

              {/* Losers */}
              <div className="col-12 mt-4">
                <div className="slnl-section-label losers">
                  <i className="bi bi-graph-down-arrow" />
                  Top Losers
                </div>
                <div className="row g-2">
                  {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="col-lg-2 col-md-4 col-6">
                          <div
                            className="skeleton"
                            style={{ height: 120, borderRadius: 10 }}
                          />
                        </div>
                      ))
                    : topLosers.map((c) => (
                        <div key={c.symbol} className="col-lg-2 col-md-4 col-6">
                          <StockCard c={c} type="loser" navigate={navigate} />
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { display: inline-block; animation: spin 0.8s linear infinite; }
      `}</style>
    </>
  );
}

export default SLNScanLive;
